import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  Star, 
  Lightning,
  CheckCircle,
  XCircle,
  Warning,
  Spinner,
  ArrowsClockwise,
  Funnel,
  CaretRight
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";

const ScoreBar = ({ label, score, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-emerald-500",
    yellow: "bg-amber-500",
    red: "bg-red-500",
    purple: "bg-purple-500",
  };
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-300 font-mono">{score}/10</span>
      </div>
      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${(score / 10) * 100}%` }}
        />
      </div>
    </div>
  );
};

const ScoredConversation = ({ conversation, onClick }) => {
  const scores = conversation.quality_scores || {};
  const recommendation = scores.recommendation || 'review';
  
  const recColors = {
    include: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle },
    exclude: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
    review: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Warning },
  };
  
  const { bg, text, icon: Icon } = recColors[recommendation] || recColors.review;
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 border border-zinc-800 rounded-md hover:border-zinc-700 transition-colors"
      data-testid={`scored-conversation-${conversation.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-zinc-200 truncate">
            {conversation.title || 'Untitled'}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {conversation.message_count} messages • {conversation.source_platform}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-lg font-mono font-semibold text-zinc-100">
              {scores.overall || '?'}
            </p>
            <p className="text-xs text-zinc-500">overall</p>
          </div>
          
          <div className={`p-2 rounded-md ${bg}`}>
            <Icon weight="fill" className={`w-5 h-5 ${text}`} />
          </div>
        </div>
      </div>
      
      {scores.reason && (
        <p className="text-xs text-zinc-500 mt-2 italic truncate">
          "{scores.reason}"
        </p>
      )}
    </button>
  );
};

const ConversationScoreDetail = ({ conversation }) => {
  const scores = conversation?.quality_scores || {};
  
  if (!conversation) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Star weight="thin" className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">Select a conversation to view scores</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-medium text-zinc-100 mb-1">
          {conversation.title || 'Untitled'}
        </h3>
        <p className="text-sm text-zinc-500">
          {conversation.message_count} messages • {conversation.source_platform}
        </p>
      </div>
      
      {/* Overall Score */}
      <div className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-lg text-center">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Overall Score</p>
        <p className="text-5xl font-mono font-bold text-zinc-100">
          {scores.overall || '—'}
        </p>
        <p className="text-sm text-zinc-400 mt-2">out of 10</p>
      </div>
      
      {/* Individual Scores */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
          Score Breakdown
        </h4>
        <ScoreBar label="Clarity" score={scores.clarity || 0} color="blue" />
        <ScoreBar label="Completeness" score={scores.completeness || 0} color="green" />
        <ScoreBar label="Relevance" score={scores.relevance || 0} color="purple" />
        <ScoreBar label="Instructiveness" score={scores.instructiveness || 0} color="yellow" />
        <ScoreBar label="Diversity" score={scores.diversity || 0} color="blue" />
      </div>
      
      {/* Recommendation */}
      <div className={`p-4 rounded-lg ${
        scores.recommendation === 'include' ? 'bg-emerald-500/10 border border-emerald-500/30' :
        scores.recommendation === 'exclude' ? 'bg-red-500/10 border border-red-500/30' :
        'bg-amber-500/10 border border-amber-500/30'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          {scores.recommendation === 'include' && <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" />}
          {scores.recommendation === 'exclude' && <XCircle weight="fill" className="w-5 h-5 text-red-400" />}
          {scores.recommendation === 'review' && <Warning weight="fill" className="w-5 h-5 text-amber-400" />}
          <span className={`font-medium capitalize ${
            scores.recommendation === 'include' ? 'text-emerald-300' :
            scores.recommendation === 'exclude' ? 'text-red-300' : 'text-amber-300'
          }`}>
            {scores.recommendation || 'Pending Review'}
          </span>
        </div>
        {scores.reason && (
          <p className={`text-sm ${
            scores.recommendation === 'include' ? 'text-emerald-400/70' :
            scores.recommendation === 'exclude' ? 'text-red-400/70' : 'text-amber-400/70'
          }`}>
            {scores.reason}
          </p>
        )}
      </div>
    </div>
  );
};

export default function QualityScoring() {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("all");
  const [scoredConversations, setScoredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scoring, setScoring] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState("all");
  const [ollamaConnected, setOllamaConnected] = useState(false);

  useEffect(() => {
    loadDatasets();
    checkOllama();
  }, []);

  useEffect(() => {
    loadScoredConversations();
  }, [filter]);

  const checkOllama = async () => {
    try {
      const status = await api.getOllamaStatus();
      setOllamaConnected(status.connected);
    } catch (error) {
      setOllamaConnected(false);
    }
  };

  const loadDatasets = async () => {
    try {
      const data = await api.getDatasets();
      setDatasets(data);
    } catch (error) {
      console.error("Failed to load datasets:", error);
    }
  };

  const loadScoredConversations = async () => {
    setLoading(true);
    try {
      const recommendation = filter === "all" ? undefined : filter;
      const data = await api.getScoredConversations(0, recommendation);
      setScoredConversations(data.conversations || []);
    } catch (error) {
      console.error("Failed to load scored conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const startBatchScoring = async () => {
    if (!ollamaConnected) {
      toast.error("Ollama is not connected. Check Settings.");
      return;
    }

    setScoring(true);
    setProgress(0);

    try {
      const datasetId = selectedDataset === "all" ? undefined : selectedDataset;
      const result = await api.batchScore(datasetId, 10);
      
      toast.success(`Scored ${result.processed} conversations`);
      loadScoredConversations();
    } catch (error) {
      console.error("Batch scoring failed:", error);
      toast.error("Failed to score conversations");
    } finally {
      setScoring(false);
    }
  };

  const stats = {
    total: scoredConversations.length,
    include: scoredConversations.filter(c => c.quality_scores?.recommendation === 'include').length,
    exclude: scoredConversations.filter(c => c.quality_scores?.recommendation === 'exclude').length,
    review: scoredConversations.filter(c => c.quality_scores?.recommendation === 'review').length,
  };

  return (
    <div>
      <PageHeader 
        title="Quality Scoring" 
        subtitle="AI-powered quality assessment for training data"
      />
      
      <div className="p-8 space-y-6">
        {/* Ollama Status Warning */}
        {!ollamaConnected && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-md flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Warning weight="fill" className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-sm text-amber-300">Ollama not connected</p>
                <p className="text-xs text-amber-400/70">Quality scoring requires a local Ollama instance</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/settings'}>
              Configure
            </Button>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="stat-card">
            <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Scored</p>
            <p className="text-2xl font-mono text-zinc-100 mt-1">{stats.total}</p>
          </div>
          <div className="stat-card border-emerald-500/30">
            <p className="text-xs text-emerald-400 uppercase tracking-wider">Include</p>
            <p className="text-2xl font-mono text-emerald-400 mt-1">{stats.include}</p>
          </div>
          <div className="stat-card border-amber-500/30">
            <p className="text-xs text-amber-400 uppercase tracking-wider">Review</p>
            <p className="text-2xl font-mono text-amber-400 mt-1">{stats.review}</p>
          </div>
          <div className="stat-card border-red-500/30">
            <p className="text-xs text-red-400 uppercase tracking-wider">Exclude</p>
            <p className="text-2xl font-mono text-red-400 mt-1">{stats.exclude}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="chart-container">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
              Batch Scoring
            </h3>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={selectedDataset} onValueChange={setSelectedDataset}>
              <SelectTrigger className="w-[200px] bg-zinc-900 border-zinc-800">
                <SelectValue placeholder="Select dataset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Datasets</SelectItem>
                {datasets.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button
              onClick={startBatchScoring}
              disabled={scoring || !ollamaConnected}
              data-testid="start-scoring-btn"
            >
              {scoring ? (
                <>
                  <Spinner weight="bold" className="w-4 h-4 mr-2 animate-spin" />
                  Scoring...
                </>
              ) : (
                <>
                  <Lightning weight="fill" className="w-4 h-4 mr-2" />
                  Score Next 10
                </>
              )}
            </Button>
            
            <Button variant="outline" onClick={loadScoredConversations}>
              <ArrowsClockwise weight="bold" className="w-4 h-4" />
            </Button>
          </div>
          
          {scoring && <Progress value={progress} className="h-1 mt-4" />}
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="chart-container">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider">
                Scored Conversations
              </h3>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800">
                  <Funnel weight="regular" className="w-4 h-4 mr-2 text-zinc-500" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="include">Include</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="exclude">Exclude</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <ScrollArea className="h-[500px]">
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-20 bg-zinc-800 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : scoredConversations.length > 0 ? (
                <div className="space-y-2">
                  {scoredConversations.map(conv => (
                    <ScoredConversation
                      key={conv.id}
                      conversation={conv}
                      onClick={() => setSelectedConversation(conv)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Star weight="thin" className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">No scored conversations yet</p>
                  <p className="text-zinc-600 text-xs mt-1">Run batch scoring to get started</p>
                </div>
              )}
            </ScrollArea>
          </div>
          
          {/* Detail */}
          <div className="chart-container">
            <ConversationScoreDetail conversation={selectedConversation} />
          </div>
        </div>
      </div>
    </div>
  );
}
